import type { PricingBreakdown } from "./pricing-utils";

/** Keep the server's code/status intact; unavailable dates are not transient. */
export class QuoteError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly minNights?: number,
  ) {
    super(message);
    this.name = "QuoteError";
  }

  get needsDateChange() {
    return this.code === "AVAILABILITY_ERROR" || this.code === "MINIMUM_NIGHTS_ERROR";
  }
}

export async function requestQuote(params: URLSearchParams, signal?: AbortSignal): Promise<PricingBreakdown> {
  const response = await fetch(`/api/quote?${params}`, { credentials: "include", signal, cache: "no-store" });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new QuoteError(body?.details || body?.message || "Unable to calculate pricing. Please try again.", response.status, body?.error, body?.minNights);
  }
  if (!body || !Number.isFinite(Number(body.total)) || body.total == null || !Array.isArray(body.breakdown)) {
    throw new QuoteError("Invalid pricing response. Please try again.", 502);
  }
  // Never offer checkout for a silently corrected range.
  if (body.correctedDates && (body.correctedDates.start !== params.get("start") || body.correctedDates.end !== params.get("end"))) {
    throw new QuoteError("Please select valid future dates.", 400, "AVAILABILITY_ERROR");
  }
  return body;
}

export function retryQuote(failureCount: number, error: Error) {
  if (error instanceof QuoteError && (error.needsDateChange || (error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429))) return false;
  return failureCount < 2;
}